import { getExecutorApi } from '@/core/api/generatedApiClient';
import { rethrowHandledApiError } from '@/core/api/rethrowHandledApiError';
import { normalizePersonSearchQuery } from '@/shared/lib/phone';
import type {
    CreateExecutorByAdminRequest,
    ExecutorApiGetExecutorsRequest,
    ExecutorResponse,
    GetExecutorsResponse,
    PassportDetailsRequest,
    RocketExecutorResponse,
    UpdateExecutorByAdminRequest,
} from '@/core/generated/api';

/** Выполняет операции с профилями исполнителей */
export const executorService = {
    /** Регистрирует паспортные данные существующего исполнителя */
    async registerPassportDetails(id: string, data: PassportDetailsRequest): Promise<void> {
        try {
            await getExecutorApi()
                .registrationPassportDetails({
                    executorId: id,
                    passportDetailsRequest: data,
                });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },

    /** Создаёт профиль исполнителя */
    async create(data: CreateExecutorByAdminRequest): Promise<ExecutorResponse> {
        try {
            const response = await getExecutorApi().createExecutorByAdmin({ createExecutorByAdminRequest: data });

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Обновляет профиль исполнителя */
    async update(id: string, data: UpdateExecutorByAdminRequest): Promise<ExecutorResponse> {
        try {
            const response = await getExecutorApi()
                .updateExecutorByAdmin({
                    executorId: id,
                    updateExecutorByAdminRequest: data,
                });

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Загружает страницу исполнителей */
    async getList(request: ExecutorApiGetExecutorsRequest): Promise<GetExecutorsResponse> {
        try {
            const response = await getExecutorApi().getExecutors(request);

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Загружает профиль исполнителя */
    async getById(id: string): Promise<ExecutorResponse> {
        try {
            const response = await getExecutorApi().getExecutorById({ executorId: id });

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Ищет профиль исполнителя в Rocket Work */
    async searchRocketWorkByPhone(phone: string): Promise<RocketExecutorResponse> {
        try {
            const response = await getExecutorApi()
                .searchExecutorInRocketWorkByPhone({
                    phone: normalizePersonSearchQuery(phone),
                });

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Деактивирует профиль исполнителя */
    async deactivate(id: string, comment?: string): Promise<void> {
        try {
            await getExecutorApi()
                .deleteExecutor({
                    executorId: id,
                    deleteRequest: { comment }
                });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },

    /** Верифицирует исполнителя */
    async verify(id: string): Promise<void> {
        try {
            await getExecutorApi().verifyExecutor({ executorId: id });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },

    /** Назначает штраф исполнителю */
    async assignPenalty(id: string): Promise<void> {
        try {
            await getExecutorApi().assignExecutorPenalty({ executorId: id });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },

    /** Снимает штраф исполнителя */
    async removePenalty(id: string): Promise<void> {
        try {
            await getExecutorApi().removeExecutorPenalty({ executorId: id });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },

    /** Блокирует профиль исполнителя */
    async block(id: string, reason: string): Promise<void> {
        try {
            await getExecutorApi()
                .blockExecutor({
                    executorId: id,
                    blockRequest: { reason }
                });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },

    /** Разблокирует профиль исполнителя */
    async unblock(id: string, reason: string): Promise<void> {
        try {
            await getExecutorApi()
                .unblockExecutor({
                    executorId: id,
                    unblockRequest: { reason }
                });
        } catch (error: unknown) {
            rethrowHandledApiError(error);
        }
    },
};
